import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import SeoHead from "../components/SeoHead";
import InboxPanel from "../components/InboxPanel";
import { formatInboxEmail } from "../utils/inbox";
import { formatDaysRemaining, getPlanLabel } from "../utils/plan";
import { useAuth } from "../context/AuthContext";

const ADMIN_SECTIONS = [
  { id: "dashboard", label: "Dashboard" },
  { id: "inbox", label: "Inbox" },
  { id: "accounts", label: "Accounts" },
  { id: "messages", label: "Messages" },
];

function formatDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function AdminPanel() {
  const { user, ready, login, logout, isAdmin, getAuthHeaders, launchAsUser, saveActiveInbox } =
    useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionParam = searchParams.get("section");
  const activeSection = ADMIN_SECTIONS.some((item) => item.id === sectionParam)
    ? sectionParam
    : "dashboard";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState("");
  const [contactMessages, setContactMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messagesError, setMessagesError] = useState("");
  const [expandedMessageId, setExpandedMessageId] = useState(null);
  const [actionId, setActionId] = useState(null);
  const [snack, setSnack] = useState(null);
  const snackTimeoutRef = useRef(null);

  const showSnack = (text, isError = false) => {
    if (snackTimeoutRef.current) clearTimeout(snackTimeoutRef.current);
    setSnack({ text, isError });
    snackTimeoutRef.current = setTimeout(() => {
      setSnack(null);
      snackTimeoutRef.current = null;
    }, 3000);
  };

  useEffect(() => {
    return () => {
      if (snackTimeoutRef.current) clearTimeout(snackTimeoutRef.current);
    };
  }, []);

  const setActiveSection = (sectionId) => {
    setSearchParams({ section: sectionId }, { replace: true });
  };

  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    setUsersError("");
    try {
      const response = await fetch("/api/admin/users", {
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || "Failed to load users");
      }
      setUsers(data);
    } catch (err) {
      setUsersError(err.message);
    } finally {
      setLoadingUsers(false);
    }
  }, [getAuthHeaders]);

  const fetchContactMessages = useCallback(async () => {
    setLoadingMessages(true);
    setMessagesError("");
    try {
      const response = await fetch("/api/admin/contact-messages", {
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || "Failed to load contact messages");
      }
      setContactMessages(data);
    } catch (err) {
      setMessagesError(err.message);
    } finally {
      setLoadingMessages(false);
    }
  }, [getAuthHeaders]);

  const refreshDashboard = useCallback(async () => {
    await Promise.all([fetchUsers(), fetchContactMessages()]);
  }, [fetchUsers, fetchContactMessages]);

  useEffect(() => {
    if (ready && isAdmin) {
      refreshDashboard();
    }
  }, [ready, isAdmin, refreshDashboard]);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setSubmitting(true);

    const result = await login({ email, password });
    setSubmitting(false);

    if (!result.ok) {
      setLoginError(result.error);
      return;
    }

    if (result.user?.role !== "admin") {
      logout();
      setLoginError("Admin access only. Use a valid admin account.");
    }
  };

  const runUserAction = async (userId, action) => {
    setActionId(userId);
    try {
      const isDelete = action === "delete";
      const isGrantPro = action === "grant-pro";
      const url = isDelete
        ? `/api/admin/users/${userId}`
        : isGrantPro
          ? `/api/admin/users/${userId}/grant-pro`
          : `/api/admin/users/${userId}/${action}`;
      const response = await fetch(url, {
        method: isDelete ? "DELETE" : "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || "Action failed");
      }

      if (action === "delete") {
        setUsers((prev) => prev.filter((item) => item.id !== userId));
        showSnack("Account deleted");
      } else {
        const updated = data.user;
        setUsers((prev) => prev.map((item) => (item.id === userId ? updated : item)));
        if (action === "grant-pro") {
          showSnack("Pro plan granted for 30 days");
        } else {
          showSnack(action === "block" ? "Account blocked" : "Account unblocked");
        }
      }
    } catch (err) {
      showSnack(err.message, true);
    } finally {
      setActionId(null);
    }
  };

  const handleDelete = (account) => {
    if (
      !window.confirm(
        `Delete ${account.email}? They can sign up again with the same email.`,
      )
    ) {
      return;
    }
    runUserAction(account.id, "delete");
  };

  const handleLaunch = async (account) => {
    if (account.status === "blocked") {
      showSnack("Cannot launch a blocked account.", true);
      return;
    }

    setActionId(account.id);
    const result = await launchAsUser(account.id);
    setActionId(null);

    if (!result.ok) {
      showSnack(result.error, true);
      return;
    }

    const inbox = result.user?.activeInbox;
    navigate(inbox ? `/dashboard?inbox=${encodeURIComponent(inbox)}` : "/dashboard");
  };

  const formatActiveInbox = (account) => {
    if (!account.activeInbox) return "—";
    return formatInboxEmail(
      account.activeInbox,
      account.inboxSubdomain,
      account.role === "admin",
    );
  };

  const unreadCount = contactMessages.filter((item) => !item.read).length;

  const handleToggleMessage = async (message) => {
    const isExpanded = expandedMessageId === message.id;
    setExpandedMessageId(isExpanded ? null : message.id);

    if (!message.read && !isExpanded) {
      try {
        const response = await fetch(`/api/admin/contact-messages/${message.id}/read`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
        });
        const data = await response.json().catch(() => ({}));
        if (response.ok) {
          setContactMessages((prev) =>
            prev.map((item) => (item.id === message.id ? data.message : item)),
          );
        }
      } catch {
        // keep message visible even if mark-read fails
      }
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm("Delete this contact message?")) return;

    setActionId(messageId);
    try {
      const response = await fetch(`/api/admin/contact-messages/${messageId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || "Failed to delete message");
      }
      setContactMessages((prev) => prev.filter((item) => item.id !== messageId));
      if (expandedMessageId === messageId) setExpandedMessageId(null);
      showSnack("Message deleted");
    } catch (err) {
      showSnack(err.message, true);
    } finally {
      setActionId(null);
    }
  };

  const renderDashboard = () => (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-white">Dashboard</h2>
        <p className="mt-1 text-sm text-zinc-400">Overview of sign-ups and contact activity.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-5">
          <p className="text-xs uppercase tracking-wide text-zinc-500">Total sign-ups</p>
          <p className="mt-2 text-3xl font-bold text-white">{users.length}</p>
        </div>
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-5">
          <p className="text-xs uppercase tracking-wide text-zinc-500">Active users</p>
          <p className="mt-2 text-3xl font-bold text-emerald-400">
            {users.filter((item) => item.status === "active").length}
          </p>
        </div>
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-5">
          <p className="text-xs uppercase tracking-wide text-zinc-500">Blocked</p>
          <p className="mt-2 text-3xl font-bold text-red-400">
            {users.filter((item) => item.status === "blocked").length}
          </p>
        </div>
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-5">
          <p className="text-xs uppercase tracking-wide text-zinc-500">New messages</p>
          <p className="mt-2 text-3xl font-bold text-sky-400">{unreadCount}</p>
        </div>
      </div>

      <div className="mt-8 grid gap-4 lg:grid-cols-2">
        <button
          type="button"
          onClick={() => setActiveSection("accounts")}
          className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-5 text-left transition hover:border-zinc-700"
        >
          <p className="text-sm font-medium text-white">Manage accounts</p>
          <p className="mt-1 text-xs text-zinc-500">
            View sign-ups, launch sessions, block or delete users.
          </p>
        </button>
        <button
          type="button"
          onClick={() => setActiveSection("messages")}
          className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-5 text-left transition hover:border-zinc-700"
        >
          <p className="text-sm font-medium text-white">View messages</p>
          <p className="mt-1 text-xs text-zinc-500">
            {unreadCount > 0
              ? `${unreadCount} unread contact message${unreadCount === 1 ? "" : "s"}.`
              : "No unread contact messages."}
          </p>
        </button>
      </div>
    </div>
  );

  const renderInbox = () => (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-white">Admin email inbox</h2>
        <p className="mt-1 text-sm text-zinc-400">
          Generate and receive emails at{" "}
          <span className="font-mono text-emerald-400">@ghostinbox.xyz</span> (no subdomain).
        </p>
      </div>
      <InboxPanel
        user={user}
        isAdminUser
        getAuthHeaders={getAuthHeaders}
        saveActiveInbox={saveActiveInbox}
      />
    </div>
  );

  const renderAccounts = () => (
    <div>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Accounts</h2>
          <p className="mt-1 text-sm text-zinc-400">
            Manage user sign-ups, launch accounts, and control access.
          </p>
        </div>
        <button
          type="button"
          onClick={fetchUsers}
          disabled={loadingUsers}
          className="self-start rounded-lg border border-zinc-700 px-4 py-2 text-sm text-zinc-300 hover:border-zinc-500 disabled:opacity-60"
        >
          {loadingUsers ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {usersError && <p className="mb-4 text-sm text-red-400">{usersError}</p>}

      <div className="overflow-x-auto rounded-xl border border-zinc-800">
        <table className="min-w-full divide-y divide-zinc-800 text-sm">
          <thead className="bg-zinc-900/80">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-zinc-400">Name</th>
              <th className="px-4 py-3 text-left font-medium text-zinc-400">Email</th>
              <th className="px-4 py-3 text-left font-medium text-zinc-400">Signed up</th>
              <th className="px-4 py-3 text-left font-medium text-zinc-400">Plan</th>
              <th className="px-4 py-3 text-left font-medium text-zinc-400">Current email in use</th>
              <th className="px-4 py-3 text-left font-medium text-zinc-400">Status</th>
              <th className="px-4 py-3 text-right font-medium text-zinc-400">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {users.map((account) => (
              <tr key={account.id} className="hover:bg-zinc-900/40">
                <td className="px-4 py-3 text-zinc-200">{account.name}</td>
                <td className="px-4 py-3 font-mono text-zinc-300">{account.email}</td>
                <td className="px-4 py-3 text-zinc-400">{formatDate(account.createdAt)}</td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      getPlanLabel(account) === "Pro"
                        ? "bg-violet-950/60 text-violet-300 ring-1 ring-violet-800/60"
                        : getPlanLabel(account) === "Free"
                          ? "bg-sky-950/60 text-sky-300 ring-1 ring-sky-800/60"
                          : "bg-zinc-800/60 text-zinc-400 ring-1 ring-zinc-700/60"
                    }`}
                  >
                    {getPlanLabel(account)}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-emerald-300/90">
                  {formatActiveInbox(account)}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      account.status === "blocked"
                        ? "bg-red-950/60 text-red-300 ring-1 ring-red-800/60"
                        : "bg-emerald-950/60 text-emerald-300 ring-1 ring-emerald-800/60"
                    }`}
                  >
                    {account.status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      disabled={actionId === account.id || account.status === "blocked"}
                      onClick={() => handleLaunch(account)}
                      className="rounded-lg border border-sky-700/60 px-3 py-1.5 text-xs text-sky-300 hover:border-sky-500 disabled:opacity-60"
                      title={
                        account.activeInbox
                          ? `Open ${formatActiveInbox(account)}`
                          : "Open customer dashboard"
                      }
                    >
                      Launch
                    </button>
                    <button
                      type="button"
                      disabled={actionId === account.id}
                      onClick={() => runUserAction(account.id, "grant-pro")}
                      className="rounded-lg border border-violet-700/60 px-3 py-1.5 text-xs text-violet-300 hover:border-violet-500 disabled:opacity-60"
                      title="Grant Pro plan for 30 days"
                    >
                      Grant Pro
                    </button>
                    {account.status === "active" ? (
                      <button
                        type="button"
                        disabled={actionId === account.id}
                        onClick={() => runUserAction(account.id, "block")}
                        className="rounded-lg border border-amber-700/60 px-3 py-1.5 text-xs text-amber-300 hover:border-amber-500 disabled:opacity-60"
                      >
                        Block
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={actionId === account.id}
                        onClick={() => runUserAction(account.id, "unblock")}
                        className="rounded-lg border border-emerald-700/60 px-3 py-1.5 text-xs text-emerald-300 hover:border-emerald-500 disabled:opacity-60"
                      >
                        Unblock
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={actionId === account.id}
                      onClick={() => handleDelete(account)}
                      className="rounded-lg border border-red-800/60 px-3 py-1.5 text-xs text-red-300 hover:border-red-600 disabled:opacity-60"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {!loadingUsers && users.length === 0 && !usersError && (
          <p className="px-4 py-8 text-center text-sm text-zinc-500">No sign-ups yet.</p>
        )}
      </div>
    </div>
  );

  const renderMessages = () => (
    <div>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Messages</h2>
          <p className="mt-1 text-sm text-zinc-400">
            Contact form submissions from the website.
          </p>
        </div>
        <button
          type="button"
          onClick={fetchContactMessages}
          disabled={loadingMessages}
          className="self-start rounded-lg border border-zinc-700 px-4 py-2 text-sm text-zinc-300 hover:border-zinc-500 disabled:opacity-60"
        >
          {loadingMessages ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {messagesError && <p className="mb-4 text-sm text-red-400">{messagesError}</p>}

      <div className="space-y-3">
        {contactMessages.map((item) => (
          <div
            key={item.id}
            className={`rounded-xl border p-4 transition ${
              item.read
                ? "border-zinc-800 bg-zinc-900/30"
                : "border-sky-800/60 bg-sky-950/20"
            }`}
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <button
                type="button"
                onClick={() => handleToggleMessage(item)}
                className="flex-1 text-left"
              >
                <div className="flex flex-wrap items-center gap-2">
                  {!item.read && (
                    <span className="inline-flex rounded-full bg-sky-500/20 px-2 py-0.5 text-xs font-medium text-sky-300">
                      New
                    </span>
                  )}
                  <span className="font-medium text-white">{item.name}</span>
                  <span className="font-mono text-sm text-zinc-400">{item.email}</span>
                </div>
                <p className="mt-1 text-xs text-zinc-500">{formatDate(item.createdAt)}</p>
                {expandedMessageId !== item.id && (
                  <p className="mt-2 line-clamp-2 text-sm text-zinc-400">{item.message}</p>
                )}
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleToggleMessage(item)}
                  className="rounded-lg border border-zinc-700 px-3 py-1.5 text-xs text-zinc-300 hover:border-zinc-500"
                >
                  {expandedMessageId === item.id ? "Collapse" : "View"}
                </button>
                <button
                  type="button"
                  disabled={actionId === item.id}
                  onClick={() => handleDeleteMessage(item.id)}
                  className="rounded-lg border border-red-800/60 px-3 py-1.5 text-xs text-red-300 hover:border-red-600 disabled:opacity-60"
                >
                  Delete
                </button>
              </div>
            </div>
            {expandedMessageId === item.id && (
              <div className="mt-4 rounded-lg border border-zinc-800 bg-zinc-950/60 p-4 text-sm leading-relaxed text-zinc-300 whitespace-pre-wrap">
                {item.message}
              </div>
            )}
          </div>
        ))}
      </div>

      {!loadingMessages && contactMessages.length === 0 && !messagesError && (
        <p className="py-8 text-center text-sm text-zinc-500">No contact messages yet.</p>
      )}
    </div>
  );

  const renderSection = () => {
    switch (activeSection) {
      case "inbox":
        return renderInbox();
      case "accounts":
        return renderAccounts();
      case "messages":
        return renderMessages();
      default:
        return renderDashboard();
    }
  };

  if (!ready) {
    return (
      <Layout>
        <div className="flex min-h-[50vh] items-center justify-center text-zinc-400">
          Loading…
        </div>
      </Layout>
    );
  }

  if (!isAdmin) {
    return (
      <Layout>
        <SeoHead title="Admin Login – Ghost Inbox" noindex />
        <div className="mx-auto flex max-w-md flex-1 flex-col justify-center px-6 py-16">
          <p className="text-xs font-medium uppercase tracking-wider text-emerald-400">
            Admin
          </p>
          <h1 className="mt-2 text-2xl font-bold text-white">Admin login</h1>
          <p className="mt-2 text-sm text-zinc-400">
            Sign in to manage user accounts and sign-ups.
          </p>

          <form onSubmit={handleAdminLogin} className="mt-8 space-y-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-zinc-400">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full rounded-lg border border-zinc-700 bg-zinc-900 px-4 py-2.5 text-white focus:border-emerald-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-zinc-400">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full rounded-lg border border-zinc-700 bg-zinc-900 px-4 py-2.5 text-white focus:border-emerald-500 focus:outline-none"
              />
            </div>
            {loginError && <p className="text-sm text-red-400">{loginError}</p>}
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-lg bg-emerald-500 py-3 text-sm font-semibold text-zinc-950 hover:bg-emerald-400 disabled:opacity-60"
            >
              {submitting ? "Signing in…" : "Sign in to admin"}
            </button>
          </form>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SeoHead title="Admin Dashboard – Ghost Inbox" noindex />

      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 py-8 lg:flex-row lg:px-6 lg:py-10">
        <aside className="w-full shrink-0 lg:w-56">
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-4 lg:sticky lg:top-6">
            <p className="text-xs font-medium uppercase tracking-wider text-emerald-400">
              Admin
            </p>
            <p className="mt-1 truncate text-sm text-zinc-400">{user?.email}</p>

            <nav className="mt-6 space-y-1">
              {ADMIN_SECTIONS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveSection(item.id)}
                  className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm font-medium transition ${
                    activeSection === item.id
                      ? "bg-emerald-500/15 text-emerald-400"
                      : "text-zinc-300 hover:bg-zinc-800/60 hover:text-white"
                  }`}
                >
                  <span>{item.label}</span>
                  {item.id === "messages" && unreadCount > 0 && (
                    <span className="rounded-full bg-sky-500/20 px-2 py-0.5 text-xs text-sky-300">
                      {unreadCount}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <div className="mb-6 lg:hidden">
            <h1 className="text-2xl font-bold text-white">Admin panel</h1>
          </div>

          {snack && (
            <div
              className={`mb-4 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                snack.isError
                  ? "border-red-800/60 bg-red-950/40 text-red-300"
                  : "border-emerald-800/60 bg-emerald-950/40 text-emerald-300"
              }`}
              role="status"
            >
              {snack.text}
            </div>
          )}

          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/30 p-6">
            {renderSection()}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default AdminPanel;
